import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Databases, Query } from 'appwrite';
import { AuthContext } from '../auth-context';
import client, { APPWRITE_ENDPOINT, APPWRITE_PROJECT_ID } from '../app-user';
import {
  deleteLeagueNewsItem,
  HomeContentDocument,
  listHomeContent,
  saveCommissionerNote,
  saveLeagueNewsItem,
} from '../services/homeContent';
import { getLeagueSettings, updateRfaPage, updateSalaryCap } from '../services/leagueSettings';
import styles from '../styles/admin.module.css';
import { calculateBidValue, compareFreeAgentBids } from '../utils/freeAgencyBids';

const databases = new Databases(client);

// Define a helper function to check and log if default values are being used
function getEnvVariable(name: string, defaultValue: string) {
  const value = process.env[name];
  if (value === undefined) {
    console.warn(`Environment variable ${name} is not set. Using default value: ${defaultValue}`);
    return defaultValue;
  }
  return value;
}

// Use your existing helper function to get environment variables
const DATABASE_ID = getEnvVariable('REACT_APP_DATABASE_ID', 'database');
const ROSTERED_PLAYER_DATA_COLLECTION_ID = getEnvVariable('REACT_APP_ROSTERED_PLAYER_DATA_COLLECTION_ID', 'roster data');
const RFA_BID_COLLECTION_ID = getEnvVariable('REACT_APP_RFA_BIDS_COLLECTION_ID', 'RFA bids');
const FREE_AGENTS_DATA_COLLECTION_ID = getEnvVariable('REACT_APP_FREE_AGENTS_DATA_COLLECTION_ID', 'free-agents-data');
const FREE_AGENTS_BIDS_COLLECTION_ID = getEnvVariable('REACT_APP_FREE_AGENTS_BIDS_COLLECTION_ID', 'free-agents-bids');
const TEAMS_COLLECTION_ID = getEnvVariable('REACT_APP_TEAMS_COLLECTION_ID', 'teams');

interface PlayerDocument {
  $id: string; // Add $id for Appwrite document ID
  roster_id: string;
  roster_status: 'starter' | 'bench' | 'reserve' | 'taxi';
  owner: string;
  team_name: string;
  playerId: string;
  name: string;
  first_name: string;
  last_name: string;
  position: string;
  team: string;
  college: string;
  number: number;
  age: number;
  fantasy_positions: string[];
  salary: number;
  duration: number;
  contract_type: string;
  restricted_free_agent?: boolean;
}

interface RFABid {
  playerId: string;
  bidAmount: number;
  bidDuration: number;
  playerName: string;
  playerPosition: string;
  owner: string;
  $createdAt: string;
}

interface FreeAgentBid {
  playerId: string;
  bidAmount: number;
  bidDuration: number;
  owner: string;
  $createdAt: string;
}

interface FreeAgentPlayer {
  playerId: string;
  name: string;
  position: string;
}

interface ImpersonationUser {
  $id: string;
  email: string;
  name: string;
}

interface Team {
  $id?: string;
  name: string;
  sleeperID: string;
}

interface AuctionWinner {
  playerId: string;
  playerName: string;
  position: string;
  winningBid: number;
  duration: number;
  winner: string;
  auctionType: 'RFA' | 'Free Agent';
}

interface RFABidWithProjection extends RFABid {
  adjustedValue: number;
  isProjectedWinner: boolean;
  currentOwner?: string;
}

interface FABidWithProjection {
  playerId: string;
  playerName: string;
  playerPosition: string;
  owner: string;
  bidAmount: number;
  bidDuration: number;
  adjustedValue: number; // same as baseValue for FA
  isProjectedWinner: boolean;
  $createdAt: string;
}

// Calculate owner bonus (20% bump for current owner)
const calculateOwnerBonus = (adjustedValue: number, isCurrentOwner: boolean): number => {
  return isCurrentOwner ? adjustedValue * 1.2 : adjustedValue;
};

const Admin: React.FunctionComponent = () => {
  const [rfaWinners, setRfaWinners] = useState<AuctionWinner[]>([]);
  const [freeAgentWinners, setFreeAgentWinners] = useState<AuctionWinner[]>([]);
  const [isProcessingRFA, setIsProcessingRFA] = useState(false);
  const [isProcessingFA, setIsProcessingFA] = useState(false);
  const [showRFAResults, setShowRFAResults] = useState(false);
  const [showFAResults, setShowFAResults] = useState(false);
  const [isExportingRFA, setIsExportingRFA] = useState(false);
  const [isExportingFA, setIsExportingFA] = useState(false); // NEW
  const [isCalculatingRFAWinners, setIsCalculatingRFAWinners] = useState(false);
  const [isCalculatingFAWinners, setIsCalculatingFAWinners] = useState(false);
  const [rfaPageStatus, setRfaPageStatus] = useState<'Selection Process' | 'Blind Auction' | 'Off'>('Off');
  const [isUpdatingRFAStatus, setIsUpdatingRFAStatus] = useState(false);
  const [isClearingAllRFAFlags, setIsClearingAllRFAFlags] = useState(false);
  const [isReducingContracts, setIsReducingContracts] = useState(false);
  const [contractCutoffDate, setContractCutoffDate] = useState('');
  const { user, impersonatedUserId, startImpersonation, stopImpersonation } = useContext(AuthContext);
  const [availableUsers, setAvailableUsers] = useState<ImpersonationUser[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [userLoadError, setUserLoadError] = useState('');
  const [commissionerNote, setCommissionerNote] = useState('');
  const [leagueNewsItems, setLeagueNewsItems] = useState<HomeContentDocument[]>([]);
  const [newsTitle, setNewsTitle] = useState('');
  const [newsSummary, setNewsSummary] = useState('');
  const [newsUrl, setNewsUrl] = useState('');
  const [editingNewsId, setEditingNewsId] = useState<string | null>(null);
  const [isLoadingHomeContent, setIsLoadingHomeContent] = useState(false);
  const [isSavingHomeContent, setIsSavingHomeContent] = useState(false);
  const [homeContentMessage, setHomeContentMessage] = useState('');
  const [salaryCapInput, setSalaryCapInput] = useState('188');
  const [isSavingSalaryCap, setIsSavingSalaryCap] = useState(false);
  const [salaryCapMessage, setSalaryCapMessage] = useState('');

  const selectedImpersonationUser = useMemo(
    () => availableUsers.find((availableUser) => availableUser.$id === impersonatedUserId),
    [availableUsers, impersonatedUserId]
  );
  const isAdmin = user.session?.labels.includes('admin');

  const loadAdminHomeContent = async () => {
    setIsLoadingHomeContent(true);
    setHomeContentMessage('');
    try {
      const content = await listHomeContent(true);
      const note = content.find((item) => item.contentType === 'commissioner_note');
      setCommissionerNote(note?.body || '');
      setLeagueNewsItems(content.filter((item) => item.contentType === 'league_news'));
    } catch (error) {
      console.error('Unable to load homepage content:', error);
      setHomeContentMessage('Unable to load homepage content. Create the home-content Appwrite collection and confirm its permissions.');
    } finally {
      setIsLoadingHomeContent(false);
    }
  };

  useEffect(() => {
    if (isAdmin) loadAdminHomeContent();
    // This editor reloads when the active session gains or loses admin access.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    getLeagueSettings().then((settings) => {
      setSalaryCapInput(String(settings.salaryCap));
      setRfaPageStatus(
        settings.rfaPage === 'selection_process'
          ? 'Selection Process'
          : settings.rfaPage === 'blind_auction'
            ? 'Blind Auction'
            : 'Off'
      );
    });
  }, [isAdmin]);

  const handleSaveSalaryCap = async () => {
    const nextCap = Number(salaryCapInput);
    if (!Number.isFinite(nextCap) || nextCap < 0) {
      setSalaryCapMessage('Enter a valid cap of zero or more.');
      return;
    }
    setIsSavingSalaryCap(true);
    setSalaryCapMessage('');
    try {
      const settings = await updateSalaryCap(nextCap, user.session?.email || user.session?.name || 'admin');
      setSalaryCapInput(String(settings.salaryCap));
      setSalaryCapMessage(`Salary cap updated to $${settings.salaryCap}.`);
    } catch (error) {
      console.error('Unable to update salary cap:', error);
      setSalaryCapMessage('Unable to update the salary cap. Check Appwrite permissions.');
    } finally {
      setIsSavingSalaryCap(false);
    }
  };

  const handleSaveCommissionerNote = async () => {
    if (!commissionerNote.trim()) return;
    setIsSavingHomeContent(true);
    setHomeContentMessage('');
    try {
      await saveCommissionerNote(commissionerNote.trim());
      setHomeContentMessage('Commissioner note saved.');
    } catch (error) {
      console.error('Unable to save commissioner note:', error);
      setHomeContentMessage('Unable to save the commissioner note. Check the home-content collection schema and permissions.');
    } finally {
      setIsSavingHomeContent(false);
    }
  };

  const resetNewsForm = () => {
    setEditingNewsId(null);
    setNewsTitle('');
    setNewsSummary('');
    setNewsUrl('');
  };

  const handleSaveLeagueNews = async () => {
    if (!newsTitle.trim() || !newsSummary.trim()) return;
    setIsSavingHomeContent(true);
    setHomeContentMessage('');
    try {
      await saveLeagueNewsItem({
        $id: editingNewsId || undefined,
        title: newsTitle.trim(),
        body: newsSummary.trim(),
        url: newsUrl.trim(),
        sortOrder: editingNewsId
          ? leagueNewsItems.find((item) => item.$id === editingNewsId)?.sortOrder || 1
          : leagueNewsItems.length + 1,
        published: true,
      });
      resetNewsForm();
      await loadAdminHomeContent();
      setHomeContentMessage('League news saved.');
    } catch (error) {
      console.error('Unable to save league news:', error);
      setHomeContentMessage('Unable to save league news. Check the home-content collection schema and permissions.');
    } finally {
      setIsSavingHomeContent(false);
    }
  };

  const handleEditLeagueNews = (item: HomeContentDocument) => {
    setEditingNewsId(item.$id);
    setNewsTitle(item.title);
    setNewsSummary(item.body);
    setNewsUrl(item.url || '');
  };

  const handleDeleteLeagueNews = async (documentId: string) => {
    if (!window.confirm('Delete this league news item?')) return;
    setIsSavingHomeContent(true);
    try {
      await deleteLeagueNewsItem(documentId);
      if (editingNewsId === documentId) resetNewsForm();
      await loadAdminHomeContent();
      setHomeContentMessage('League news deleted.');
    } catch (error) {
      console.error('Unable to delete league news:', error);
      setHomeContentMessage('Unable to delete league news. Check Appwrite permissions.');
    } finally {
      setIsSavingHomeContent(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;

    const loadUsers = async () => {
      setIsLoadingUsers(true);
      setUserLoadError('');
      try {
        const users: ImpersonationUser[] = [];
        const limit = 100;
        let offset = 0;

        while (true) {
          const usersUrl = new URL(`${APPWRITE_ENDPOINT.replace(/\/$/, '')}/users`);
          const result = await (client as any).call(
            'get',
            usersUrl,
            {
              'X-Appwrite-Project': APPWRITE_PROJECT_ID,
            },
            { limit, offset }
          );

          const pageUsers = result.users || [];
          users.push(...pageUsers);

          if (pageUsers.length < limit) break;
          offset += limit;
        }

        setAvailableUsers(users);
      } catch (error) {
        console.error('Failed to load impersonation users:', error);
        const message = error instanceof Error ? error.message : 'Unknown error';
        setUserLoadError(`Unable to load users: ${message}. Confirm your Appwrite account has the impersonator capability and sign out/sign back in after enabling it.`);
      } finally {
        setIsLoadingUsers(false);
      }
    };

    loadUsers();
  }, [isAdmin]);

  const clearAllRFAFlags = async () => {
    const shouldContinue = window.confirm('Set restricted_free_agent to false for every rostered player?');
    if (!shouldContinue) return;

    setIsClearingAllRFAFlags(true);
    try {
      let allPlayers: PlayerDocument[] = [];
      let offset = 0;
      const limit = 50;
      while (true) {
        const result = await databases.listDocuments(DATABASE_ID, ROSTERED_PLAYER_DATA_COLLECTION_ID, [Query.limit(limit), Query.offset(offset)]);
        const documents = result.documents as unknown as PlayerDocument[];
        allPlayers = [...allPlayers, ...documents];
        if (documents.length < limit) break;
        offset += limit;
      }

      const updateTargets = allPlayers.filter((player) => player.restricted_free_agent === true);
      let updatedCount = 0;
      let failedCount = 0;

      for (const player of updateTargets) {
        try {
          await databases.updateDocument(DATABASE_ID, ROSTERED_PLAYER_DATA_COLLECTION_ID, player.$id, { restricted_free_agent: false });
          updatedCount += 1;
        } catch (error) {
          failedCount += 1;
          console.error(`Failed to clear restricted_free_agent for ${player.name} (${player.playerId})`, error);
        }
      }

      alert(`RFA reset complete.\nTotal rostered players: ${allPlayers.length}\nUpdated to false: ${updatedCount}\nFailed: ${failedCount}`);
    } catch (error) {
      console.error('Failed to clear all RFA flags:', error);
      alert('Failed to clear RFA flags.');
    } finally {
      setIsClearingAllRFAFlags(false);
    }
  };

  const reduceContractDuration = async () => {
    if (!contractCutoffDate) {
      alert('Please select a cutoff date before reducing contract durations.');
      return;
    }

    const cutoffDateTime = new Date(`${contractCutoffDate}T23:59:59.999Z`);
    if (Number.isNaN(cutoffDateTime.getTime())) {
      alert('Invalid cutoff date selected.');
      return;
    }

    const confirmMessage = `Reduce contract duration by 1 year for all rostered players last updated on or before ${contractCutoffDate}?

This action cannot be undone.`;
    const shouldContinue = window.confirm(confirmMessage);

    if (!shouldContinue) {
      return;
    }

    setIsReducingContracts(true);

    try {
      let allDocuments: PlayerDocument[] = [];
      const limit = 50;
      let offset = 0;
      const cutoffIso = cutoffDateTime.toISOString();

      while (true) {
        const result = await databases.listDocuments(
          DATABASE_ID,
          ROSTERED_PLAYER_DATA_COLLECTION_ID,
          [Query.lessThanEqual('$updatedAt', cutoffIso), Query.limit(limit), Query.offset(offset)]
        );

        const documents = result.documents as unknown as PlayerDocument[];
        allDocuments = [...allDocuments, ...documents];

        if (documents.length < limit) {
          break;
        }

        offset += limit;
      }

      if (allDocuments.length === 0) {
        alert(`No contracts found with updated date on or before ${contractCutoffDate}.`);
        return;
      }

      const docsToUpdate = allDocuments.filter((doc) => doc.duration > 0);
      let successCount = 0;
      let failureCount = 0;

      for (const doc of docsToUpdate) {
        const newDuration = Math.max(0, doc.duration - 1);
        try {
          await databases.updateDocument(
            DATABASE_ID,
            ROSTERED_PLAYER_DATA_COLLECTION_ID,
            doc.$id,
            { duration: newDuration }
          );
          successCount += 1;
        } catch (error) {
          failureCount += 1;
          console.error(`Failed reducing contract for ${doc.name} (${doc.playerId})`, error);
        }
      }

      const zeroDurationCount = allDocuments.length - docsToUpdate.length;
      alert(`Contract reduction complete.
Eligible records: ${allDocuments.length}
Updated: ${successCount}
Skipped (already 0 years): ${zeroDurationCount}
Failed: ${failureCount}`);
    } catch (error) {
      console.error('Error reducing contract durations:', error);
      alert('Failed to reduce contract durations');
    } finally {
      setIsReducingContracts(false);
    }
  };

  const updateRfaPageStatus = async (nextStatus: 'Selection Process' | 'Blind Auction' | 'Off') => {
    setIsUpdatingRFAStatus(true);
    try {
      const statusValue = nextStatus === 'Selection Process'
        ? 'selection_process'
        : nextStatus === 'Blind Auction'
          ? 'blind_auction'
          : 'off';
      await updateRfaPage(statusValue, user.session?.email || user.session?.name || 'admin');
      setRfaPageStatus(nextStatus);
      alert(`RFA page status updated to ${nextStatus}.`);
    } catch (error) {
      console.error('Failed to update RFA page status:', error);
      alert('Failed to update RFA page status. Check League Settings permissions for update access.');
    } finally {
      setIsUpdatingRFAStatus(false);
    }
  };

  const exportRFABidsWithProjections = async () => {
    setIsExportingRFA(true);
    try {
      // Fetch all RFA bids
      let allBids: RFABid[] = [];
      let offset = 0;
      const limit = 50;

      while (true) {
        const result = await databases.listDocuments(
          DATABASE_ID,
          RFA_BID_COLLECTION_ID,
          [Query.limit(limit), Query.offset(offset), Query.orderDesc('$createdAt')]
        );

        allBids = [...allBids, ...result.documents as unknown as RFABid[]];

        if (result.documents.length < limit) {
          break;
        }

        offset += limit;
      }

      // Filter to keep only the most recent bid per player per owner
      const filteredBidsMap: Map<string, RFABid> = new Map();
      allBids.forEach(bid => {
        const key = `${bid.playerId}-${bid.owner}`;
        if (!filteredBidsMap.has(key) || new Date(bid.$createdAt) > new Date(filteredBidsMap.get(key)!.$createdAt)) {
          filteredBidsMap.set(key, bid);
        }
      });
      const filteredBids = Array.from(filteredBidsMap.values());

      // Fetch rostered players to determine current owners
      let rosteredPlayers: PlayerDocument[] = [];
      offset = 0;

      while (true) {
        const result = await databases.listDocuments(
          DATABASE_ID,
          ROSTERED_PLAYER_DATA_COLLECTION_ID,
          [Query.limit(limit), Query.offset(offset)]
        );

        rosteredPlayers = [...rosteredPlayers, ...result.documents as unknown as PlayerDocument[]];

        if (result.documents.length < limit) {
          break;
        }

        offset += limit;
      }

      // Fetch teams to map user names to sleeper IDs
      let teams: Team[] = [];
      offset = 0;

      while (true) {
        const result = await databases.listDocuments(
          DATABASE_ID,
          TEAMS_COLLECTION_ID,
          [Query.limit(limit), Query.offset(offset)]
        );

        teams = [...teams, ...result.documents as unknown as Team[]];

        if (result.documents.length < limit) {
          break;
        }

        offset += limit;
      }

      // Create maps for quick lookup
      const playerOwnerMap: Record<string, string> = {};
      rosteredPlayers.forEach(player => {
        playerOwnerMap[player.playerId] = player.owner;
      });

      const teamMap: Record<string, string> = {};
      teams.forEach(team => {
        teamMap[team.name] = team.sleeperID;
      });

      // Process filtered bids with projections
      const bidsWithProjections: RFABidWithProjection[] = filteredBids.map(bid => {
        const currentOwnerSleeperID = playerOwnerMap[bid.playerId];
        const bidderSleeperID = teamMap[bid.owner];
        const isCurrentOwner = currentOwnerSleeperID === bidderSleeperID;
        
        const baseValue = calculateBidValue(bid.bidAmount, bid.bidDuration);
        const adjustedValue = calculateOwnerBonus(baseValue, isCurrentOwner);

        return {
          ...bid,
          adjustedValue,
          isProjectedWinner: false, // Will be calculated below
          currentOwner: currentOwnerSleeperID
        };
      });

      // Group by player and determine projected winners
      const playerBidsMap: Record<string, RFABidWithProjection[]> = {};
      bidsWithProjections.forEach(bid => {
        if (!playerBidsMap[bid.playerId]) {
          playerBidsMap[bid.playerId] = [];
        }
        playerBidsMap[bid.playerId].push(bid);
      });

      // Mark projected winners
      Object.values(playerBidsMap).forEach(playerBids => {
        const sortedBids = playerBids.sort((a, b) => {
          if (b.adjustedValue !== a.adjustedValue) {
            return b.adjustedValue - a.adjustedValue;
          }
          return new Date(a.$createdAt).getTime() - new Date(b.$createdAt).getTime();
        });

        if (sortedBids.length > 0) {
          sortedBids[0].isProjectedWinner = true;
        }
      });

      // Create CSV content
      const csvContent = [
        [
          'Player ID',
          'Player Name', 
          'Position',
          'Bidder',
          'Bid Amount',
          'Duration',
          'Base Value',
          'Adjusted Value',
          'Current Owner',
          'Is Current Owner',
          'Projected Winner',
          'Created At'
        ].join(','),
        ...bidsWithProjections.map(bid => [
          bid.playerId,
          bid.playerName,
          bid.playerPosition,
          bid.owner,
          bid.bidAmount.toString(),
          bid.bidDuration.toString(),
          calculateBidValue(bid.bidAmount, bid.bidDuration).toFixed(2),
          bid.adjustedValue.toFixed(2),
          bid.currentOwner || '',
          (bid.currentOwner === teamMap[bid.owner]).toString(),
          bid.isProjectedWinner.toString(),
          bid.$createdAt
        ].join(','))
      ].join('\n');

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'rfa_bids_with_projections.csv';
      a.click();
      window.URL.revokeObjectURL(url);

      alert('RFA bids exported successfully with projected winners!');
    } catch (error) {
      console.error('Error exporting RFA bids:', error);
      alert('Failed to export RFA bids');
    } finally {
      setIsExportingRFA(false);
    }
  };

  // NEW: Export Free Agent Bids with Projections (mirrors RFA export, no owner bonus)
  const exportFABidsWithProjections = async () => {
    setIsExportingFA(true);
    try {
      let allBids: FreeAgentBid[] = [];
      let allPlayers: FreeAgentPlayer[] = [];
      let offset = 0;
      const limit = 50;

      // Fetch Free Agent bids (newest first for faster "latest" comparisons)
      while (true) {
        const result = await databases.listDocuments(
          DATABASE_ID,
          FREE_AGENTS_BIDS_COLLECTION_ID,
          [Query.limit(limit), Query.offset(offset), Query.orderDesc('$createdAt')]
        );
        allBids = [...allBids, ...result.documents as unknown as FreeAgentBid[]];
        if (result.documents.length < limit) break;
        offset += limit;
      }

      // Keep latest bid per player per owner
      const latestMap: Map<string, FreeAgentBid> = new Map();
      allBids.forEach(bid => {
        const key = `${bid.playerId}-${bid.owner}`;
        if (!latestMap.has(key) || new Date(bid.$createdAt) > new Date(latestMap.get(key)!.$createdAt)) {
          latestMap.set(key, bid);
        }
      });
      const filteredBids = Array.from(latestMap.values());

      // Fetch Free Agent player infos (names/positions)
      offset = 0;
      while (true) {
        const result = await databases.listDocuments(
          DATABASE_ID,
          FREE_AGENTS_DATA_COLLECTION_ID,
          [Query.limit(limit), Query.offset(offset)]
        );
        allPlayers = [...allPlayers, ...result.documents as unknown as FreeAgentPlayer[]];
        if (result.documents.length < limit) break;
        offset += limit;
      }

      const faMap: Record<string, FreeAgentPlayer> = {};
      allPlayers.forEach(p => { faMap[p.playerId] = p; });

      // Prepare projections (adjustedValue === baseValue for FA)
      const withProj: FABidWithProjection[] = filteredBids.map(bid => {
        const player = faMap[bid.playerId];
        const baseValue = calculateBidValue(bid.bidAmount, bid.bidDuration);
        return {
          playerId: bid.playerId,
          playerName: player?.name || '',
          playerPosition: player?.position || '',
          owner: bid.owner,
          bidAmount: bid.bidAmount,
          bidDuration: bid.bidDuration,
          adjustedValue: baseValue,
          isProjectedWinner: false,
          $createdAt: bid.$createdAt
        };
      });

      // Group by player, pick projected winners by adjustedValue then recency
      const byPlayer: Record<string, FABidWithProjection[]> = {};
      withProj.forEach(b => {
        if (!byPlayer[b.playerId]) byPlayer[b.playerId] = [];
        byPlayer[b.playerId].push(b);
      });
      Object.values(byPlayer).forEach(list => {
        list.sort(compareFreeAgentBids);
        if (list.length) list[0].isProjectedWinner = true;
      });

      // Flatten back out for CSV
      const rows = Object.values(byPlayer).flat();

      const csv = [
        [
          'Player ID',
          'Player Name',
          'Position',
          'Bidder',
          'Bid Amount',
          'Duration',
          'Base Value',
          'Adjusted Value',
          'Projected Winner',
          'Created At'
        ].join(','),
        ...rows.map(r => [
          r.playerId,
          r.playerName,
          r.playerPosition,
          r.owner,
          r.bidAmount.toString(),
          r.bidDuration.toString(),
          calculateBidValue(r.bidAmount, r.bidDuration).toFixed(2),
          r.adjustedValue.toFixed(2),
          r.isProjectedWinner.toString(),
          r.$createdAt
        ].join(','))
      ].join('\n');

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'fa_bids_with_projections.csv';
      a.click();
      window.URL.revokeObjectURL(url);

      alert('Free Agent bids exported successfully with projected winners!');
    } catch (err) {
      console.error('Error exporting Free Agent bids:', err);
      alert('Failed to export Free Agent bids');
    } finally {
      setIsExportingFA(false);
    }
  };

  const calculateAndUpdateRFAWinners = async () => {
    setIsCalculatingRFAWinners(true);
    try {
      // Fetch all necessary data
      let allRFABids: RFABid[] = [];
      let rosteredPlayers: PlayerDocument[] = [];
      let teams: Team[] = [];
      
      let offset = 0;
      const limit = 50;

      // Fetch RFA bids
      while (true) {
        const result = await databases.listDocuments(
          DATABASE_ID,
          RFA_BID_COLLECTION_ID,
          [Query.limit(limit), Query.offset(offset)]
        );

        allRFABids = [...allRFABids, ...result.documents as unknown as RFABid[]];

        if (result.documents.length < limit) {
          break;
        }

        offset += limit;
      }

      // Filter RFA bids to keep only the most recent bid per player per owner
      const filteredRFABidsMap: Map<string, RFABid> = new Map();
      allRFABids.forEach(bid => {
        const key = `${bid.playerId}-${bid.owner}`;
        if (!filteredRFABidsMap.has(key) || new Date(bid.$createdAt) > new Date(filteredRFABidsMap.get(key)!.$createdAt)) {
          filteredRFABidsMap.set(key, bid);
        }
      });
      const filteredRFABids = Array.from(filteredRFABidsMap.values());

      // Fetch rostered players
      offset = 0;
      while (true) {
        const result = await databases.listDocuments(
          DATABASE_ID,
          ROSTERED_PLAYER_DATA_COLLECTION_ID,
          [Query.limit(limit), Query.offset(offset)]
        );

        rosteredPlayers = [...rosteredPlayers, ...result.documents as unknown as PlayerDocument[]];

        if (result.documents.length < limit) {
          break;
        }

        offset += limit;
      }

      // Fetch teams
      offset = 0;
      while (true) {
        const result = await databases.listDocuments(
          DATABASE_ID,
          TEAMS_COLLECTION_ID,
          [Query.limit(limit), Query.offset(offset)]
        );

        teams = [...teams, ...result.documents as unknown as Team[]];

        if (result.documents.length < limit) {
          break;
        }

        offset += limit;
      }

      // Create lookup maps
      const playerOwnerMap: Record<string, string> = {};
      const playerDocumentIdMap: Record<string, string> = {}; // Map playerId to Appwrite $id
      rosteredPlayers.forEach(player => {
        playerOwnerMap[player.playerId] = player.owner;
        playerDocumentIdMap[player.playerId] = player.$id; // Store $id
      });

      const teamMap: Record<string, string> = {};
      teams.forEach(team => {
        teamMap[team.name] = team.sleeperID;
      });

      // Process RFA bids
      const rfaPlayerBids: Record<string, RFABid[]> = {};
      filteredRFABids.forEach(bid => {
        if (!rfaPlayerBids[bid.playerId]) {
          rfaPlayerBids[bid.playerId] = [];
        }
        rfaPlayerBids[bid.playerId].push(bid);
      });

      const rfaWinners: Array<{playerId: string, winningBid: RFABid, adjustedValue: number}> = [];
      Object.entries(rfaPlayerBids).forEach(([playerId, bids]) => {
        const bidsWithValues = bids.map(bid => {
          const currentOwnerSleeperID = playerOwnerMap[playerId];
          const bidderSleeperID = teamMap[bid.owner];
          const isCurrentOwner = currentOwnerSleeperID === bidderSleeperID;
          
          const baseValue = calculateBidValue(bid.bidAmount, bid.bidDuration);
          const adjustedValue = calculateOwnerBonus(baseValue, isCurrentOwner);

          return { bid, adjustedValue };
        });

        const sortedBids = bidsWithValues.sort((a, b) => {
          if (b.adjustedValue !== a.adjustedValue) {
            return b.adjustedValue - a.adjustedValue;
          }
          return new Date(a.bid.$createdAt).getTime() - new Date(b.bid.$createdAt).getTime();
        });

        if (sortedBids.length > 0) {
          rfaWinners.push({
            playerId,
            winningBid: sortedBids[0].bid,
            adjustedValue: sortedBids[0].adjustedValue
          });
        }
      });

      // Update contracts for RFA winners
      const rfaUpdatePromises = rfaWinners.map(async (winner) => {
        const bidderSleeperID = teamMap[winner.winningBid.owner];
        const documentId = playerDocumentIdMap[winner.playerId]; // Get Appwrite $id

        if (documentId) {
          return databases.updateDocument(
            DATABASE_ID,
            ROSTERED_PLAYER_DATA_COLLECTION_ID,
            documentId, // Use $id here
            {
              salary: winner.winningBid.bidAmount,
              duration: winner.winningBid.bidDuration,
              owner: bidderSleeperID,
              contract_type: 'full' // Always set to 'full' for RFA winners
            }
          );
        } else {
          console.warn(`RFA player (ID: ${winner.playerId}) not found in rostered players with a document ID. Skipping update.`);
          return Promise.resolve();
        }
      });

      await Promise.all(rfaUpdatePromises);

      alert(`Successfully updated contracts for ${rfaWinners.length} RFA winners!`);
    } catch (error) {
      console.error('Error calculating and updating RFA winning bids:', error);
      alert('Failed to calculate and update RFA winning bids');
    } finally {
      setIsCalculatingRFAWinners(false);
    }
  };

  const calculateAndUpdateFAWinners = async () => {
    setIsCalculatingFAWinners(true);
    try {
      // Fetch all necessary data
      let allFABids: FreeAgentBid[] = [];
      let rosteredPlayers: PlayerDocument[] = [];
      let freeAgentPlayers: FreeAgentPlayer[] = [];
      let teams: Team[] = [];
      
      let offset = 0;
      const limit = 50;

      // Fetch Free Agent bids
      while (true) {
        const result = await databases.listDocuments(
          DATABASE_ID,
          FREE_AGENTS_BIDS_COLLECTION_ID,
          [Query.limit(limit), Query.offset(offset)]
        );

        allFABids = [...allFABids, ...result.documents as unknown as FreeAgentBid[]];

        if (result.documents.length < limit) {
          break;
        }

        offset += limit;
      }

      // Filter Free Agent bids to keep only the most recent bid per player per owner
      const filteredFABidsMap: Map<string, FreeAgentBid> = new Map();
      allFABids.forEach(bid => {
        const key = `${bid.playerId}-${bid.owner}`;
        if (!filteredFABidsMap.has(key) || new Date(bid.$createdAt) > new Date(filteredFABidsMap.get(key)!.$createdAt)) {
          filteredFABidsMap.set(key, bid);
        }
      });
      const filteredFABids = Array.from(filteredFABidsMap.values());

      // Fetch rostered players
      offset = 0;
      while (true) {
        const result = await databases.listDocuments(
          DATABASE_ID,
          ROSTERED_PLAYER_DATA_COLLECTION_ID,
          [Query.limit(limit), Query.offset(offset)]
        );

        rosteredPlayers = [...rosteredPlayers, ...result.documents as unknown as PlayerDocument[]];

        if (result.documents.length < limit) {
          break;
        }

        offset += limit;
      }

      // Fetch free agent players
      offset = 0;
      while (true) {
        const result = await databases.listDocuments(
          DATABASE_ID,
          FREE_AGENTS_DATA_COLLECTION_ID,
          [Query.limit(limit), Query.offset(offset)]
        );

        freeAgentPlayers = [...freeAgentPlayers, ...result.documents as unknown as FreeAgentPlayer[]];

        if (result.documents.length < limit) {
          break;
        }

        offset += limit;
      }

      // Fetch teams
      offset = 0;
      while (true) {
        const result = await databases.listDocuments(
          DATABASE_ID,
          TEAMS_COLLECTION_ID,
          [Query.limit(limit), Query.offset(offset)]
        );

        teams = [...teams, ...result.documents as unknown as Team[]];

        if (result.documents.length < limit) {
          break;
        }

        offset += limit;
      }

      // Create lookup maps
      const playerDocumentIdMap: Record<string, string> = {}; // Map playerId to Appwrite $id
      rosteredPlayers.forEach(player => {
        playerDocumentIdMap[player.playerId] = player.$id; // Store $id
      });

      const teamMap: Record<string, string> = {};
      teams.forEach(team => {
        teamMap[team.name] = team.sleeperID;
      });

      const freeAgentPlayerMap: Record<string, FreeAgentPlayer> = {};
      freeAgentPlayers.forEach(player => {
        freeAgentPlayerMap[player.playerId] = player;
      });

      // Process Free Agent bids
      const faPlayerBids: Record<string, FreeAgentBid[]> = {};
      filteredFABids.forEach(bid => {
        if (!faPlayerBids[bid.playerId]) {
          faPlayerBids[bid.playerId] = [];
        }
        faPlayerBids[bid.playerId].push(bid);
      });

      const faWinners: Array<{playerId: string, winningBid: FreeAgentBid, adjustedValue: number}> = [];
      Object.entries(faPlayerBids).forEach(([playerId, bids]) => {
        const bidsWithValues = bids.map(bid => {
          const baseValue = calculateBidValue(bid.bidAmount, bid.bidDuration);
          // Free agents don't have current owners, so no owner bonus
          return { bid, adjustedValue: baseValue };
        });

        const sortedBids = bidsWithValues.sort((a, b) => {
          if (b.adjustedValue !== a.adjustedValue) {
            return b.adjustedValue - a.adjustedValue;
          }
          return compareFreeAgentBids(a.bid, b.bid);
        });

        if (sortedBids.length > 0) {
          faWinners.push({
            playerId,
            winningBid: sortedBids[0].bid,
            adjustedValue: sortedBids[0].adjustedValue
          });
        }
      });

      // Update contracts for Free Agent winners (only if player already exists in rostered players)
      const faUpdatePromises = faWinners.map(async (winner) => {
        const bidderSleeperID = teamMap[winner.winningBid.owner];
        
        // Check if the player exists in the rosteredPlayers collection
        const existingPlayer = rosteredPlayers.find(p => p.playerId === winner.playerId);
        const documentId = playerDocumentIdMap[winner.playerId]; // Get Appwrite $id

        if (existingPlayer && documentId) {
          return databases.updateDocument(
            DATABASE_ID,
            ROSTERED_PLAYER_DATA_COLLECTION_ID,
            documentId, // Use $id here
            {
              salary: winner.winningBid.bidAmount,
              duration: winner.winningBid.bidDuration,
              owner: bidderSleeperID,
              contract_type: 'full' // Always set to 'full' for FA winners
            }
          );
        } else {
          // If player does not exist, do nothing as server function manages creation
          console.warn(`Free Agent player (ID: ${winner.playerId}) not found in rostered players or missing document ID. Skipping update.`);
          return Promise.resolve(); // Return a resolved promise to continue Promise.all
        }
      });

      await Promise.all(faUpdatePromises);

      alert(`Successfully updated contracts for ${faWinners.length} Free Agent winners!`);
    } catch (error) {
      console.error('Error calculating and updating Free Agent winning bids:', error);
      alert('Failed to calculate and update Free Agent winning bids');
    } finally {
      setIsCalculatingFAWinners(false);
    }
  };

  const processRFAAuction = async () => {
    setIsProcessingRFA(true);
    try {
      // Fetch all RFA bids
      let allBids: RFABid[] = [];
      let offset = 0;
      const limit = 50;

      while (true) {
        const result = await databases.listDocuments(
          DATABASE_ID,
          RFA_BID_COLLECTION_ID,
          [Query.limit(limit), Query.offset(offset), Query.orderDesc('$createdAt')]
        );

        allBids = [...allBids, ...result.documents as unknown as RFABid[]];

        if (result.documents.length < limit) {
          break;
        }

        offset += limit;
      }

      // Group bids by player and find the highest bid for each
      const playerBids: Record<string, RFABid[]> = {};
      allBids.forEach(bid => {
        if (!playerBids[bid.playerId]) {
          playerBids[bid.playerId] = [];
        }
        playerBids[bid.playerId].push(bid);
      });

      const winners: AuctionWinner[] = [];
      Object.entries(playerBids).forEach(([playerId, bids]) => {
        // Sort bids by amount (highest first), then by creation time (earliest first)
        const sortedBids = bids.sort((a, b) => {
          if (b.bidAmount !== a.bidAmount) {
            return b.bidAmount - a.bidAmount;
          }
          return new Date(a.$createdAt).getTime() - new Date(b.$createdAt).getTime();
        });

        const winningBid = sortedBids[0];
        winners.push({
          playerId,
          playerName: winningBid.playerName,
          position: winningBid.playerPosition,
          winningBid: winningBid.bidAmount,
          duration: winningBid.bidDuration,
          winner: winningBid.owner,
          auctionType: 'RFA'
        });
      });

      setRfaWinners(winners);
      setShowRFAResults(true);
    } catch (error) {
      console.error('Error processing RFA auction:', error);
      alert('Failed to process RFA auction');
    } finally {
      setIsProcessingRFA(false);
    }
  };

  const processFreeAgentAuction = async () => {
    setIsProcessingFA(true);
    try {
      // Fetch all free agent bids
      let allBids: FreeAgentBid[] = [];
      let offset = 0;
      const limit = 50;

      while (true) {
        const result = await databases.listDocuments(
          DATABASE_ID,
          FREE_AGENTS_BIDS_COLLECTION_ID,
          [Query.limit(limit), Query.offset(offset), Query.orderDesc('$createdAt')]
        );

        allBids = [...allBids, ...result.documents as unknown as FreeAgentBid[]];

        if (result.documents.length < limit) {
          break;
        }

        offset += limit;
      }

      // Fetch free agent player data for names and positions
      let allPlayers: FreeAgentPlayer[] = [];
      offset = 0;

      while (true) {
        const result = await databases.listDocuments(
          DATABASE_ID,
          FREE_AGENTS_DATA_COLLECTION_ID,
          [Query.limit(limit), Query.offset(offset)]
        );

        allPlayers = [...allPlayers, ...result.documents as unknown as FreeAgentPlayer[]];

        if (result.documents.length < limit) {
          break;
        }

        offset += limit;
      }

      // Create a map for quick player lookup
      const playerMap: Record<string, FreeAgentPlayer> = {};
      allPlayers.forEach(player => {
        playerMap[player.playerId] = player;
      });

      // Group bids by player and find the highest bid for each
      const playerBids: Record<string, FreeAgentBid[]> = {};
      allBids.forEach(bid => {
        if (!playerBids[bid.playerId]) {
          playerBids[bid.playerId] = [];
        }
        playerBids[bid.playerId].push(bid);
      });

      const winners: AuctionWinner[] = [];
      Object.entries(playerBids).forEach(([playerId, bids]) => {
        const sortedBids = bids.sort(compareFreeAgentBids);

        const winningBid = sortedBids[0];
        const player = playerMap[playerId];
        
        if (player) {
          winners.push({
            playerId,
            playerName: player.name,
            position: player.position,
            winningBid: winningBid.bidAmount,
            duration: winningBid.bidDuration,
            winner: winningBid.owner,
            auctionType: 'Free Agent'
          });
        }
      });

      setFreeAgentWinners(winners);
      setShowFAResults(true);
    } catch (error) {
      console.error('Error processing Free Agent auction:', error);
      alert('Failed to process Free Agent auction');
    } finally {
      setIsProcessingFA(false);
    }
  };

  const exportResults = (winners: AuctionWinner[], type: string) => {
    const csvContent = [
      ['Player Name', 'Position', 'Winning Bid', 'Duration', 'Winner', 'Auction Type'].join(','),
      ...winners.map(winner => [
        winner.playerName,
        winner.position,
        winner.winningBid.toString(),
        winner.duration.toString(),
        winner.winner,
        winner.auctionType
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${type}_auction_results.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className={styles.background}>
      <div className={styles.container}>
        <h1>Admin Area</h1>

        <div className={styles.section}>
          <h2>Homepage Content</h2>
          <p>Update the commissioner note and the league news links shown on the home page.</p>

          <div className={styles.contentEditor}>
            <div className={styles.editorPanel}>
              <h3>Note from the commish</h3>
              <label htmlFor="commissioner-note" className={styles.inputLabel}>Message</label>
              <textarea
                id="commissioner-note"
                className={styles.textArea}
                value={commissionerNote}
                onChange={(event) => setCommissionerNote(event.target.value)}
                placeholder="Write this week’s commissioner note..."
                rows={7}
                disabled={isLoadingHomeContent}
              />
              <button
                className={styles.actionButton}
                onClick={handleSaveCommissionerNote}
                disabled={isSavingHomeContent || isLoadingHomeContent || !commissionerNote.trim()}
              >
                {isSavingHomeContent ? 'Saving...' : 'Save Commissioner Note'}
              </button>
            </div>

            <div className={styles.editorPanel}>
              <h3>{editingNewsId ? 'Edit league news' : 'Add league news'}</h3>
              <label htmlFor="news-title" className={styles.inputLabel}>Headline</label>
              <input
                id="news-title"
                className={styles.textInput}
                value={newsTitle}
                onChange={(event) => setNewsTitle(event.target.value)}
                placeholder="Season preview"
              />
              <label htmlFor="news-summary" className={styles.inputLabel}>Summary</label>
              <textarea
                id="news-summary"
                className={styles.textArea}
                value={newsSummary}
                onChange={(event) => setNewsSummary(event.target.value)}
                placeholder="A short description shown on the home page..."
                rows={3}
              />
              <label htmlFor="news-url" className={styles.inputLabel}>Link</label>
              <input
                id="news-url"
                className={styles.textInput}
                value={newsUrl}
                onChange={(event) => setNewsUrl(event.target.value)}
                placeholder="/season-preview or https://..."
              />
              <div className={styles.editorActions}>
                <button
                  className={styles.actionButton}
                  onClick={handleSaveLeagueNews}
                  disabled={isSavingHomeContent || !newsTitle.trim() || !newsSummary.trim()}
                >
                  {isSavingHomeContent ? 'Saving...' : editingNewsId ? 'Update News Item' : 'Add News Item'}
                </button>
                {editingNewsId && <button className={styles.secondaryButton} onClick={resetNewsForm}>Cancel</button>}
              </div>
            </div>
          </div>

          {homeContentMessage && <p className={styles.contentMessage}>{homeContentMessage}</p>}

          <div className={styles.newsAdminList}>
            {leagueNewsItems.map((item) => (
              <div className={styles.newsAdminItem} key={item.$id}>
                <div>
                  <strong>{item.title}</strong>
                  <span>{item.body}</span>
                </div>
                <div className={styles.inlineActions}>
                  <button className={styles.secondaryButton} onClick={() => handleEditLeagueNews(item)}>Edit</button>
                  <button className={styles.dangerButton} onClick={() => handleDeleteLeagueNews(item.$id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.section}>
          <h2>League Settings</h2>
          <p>The salary cap is used by the dashboard, Team Sheets, and Free Agency.</p>
          <div className={styles.capEditor}>
            <label htmlFor="salary-cap" className={styles.inputLabel}>Salary cap</label>
            <div className={styles.currencyInputWrap}>
              <span aria-hidden="true">$</span>
              <input
                id="salary-cap"
                type="number"
                min="0"
                step="1"
                className={styles.textInput}
                value={salaryCapInput}
                onChange={(event) => setSalaryCapInput(event.target.value)}
              />
            </div>
            <button className={styles.actionButton} onClick={handleSaveSalaryCap} disabled={isSavingSalaryCap}>
              {isSavingSalaryCap ? 'Saving...' : 'Update Salary Cap'}
            </button>
          </div>
          {salaryCapMessage && <p className={styles.contentMessage}>{salaryCapMessage}</p>}
        </div>

        <div className={styles.section}>
          <h2>Impersonate User</h2>
          <p>Select a user to interact with the app using that user's Appwrite permissions. Choose Stop impersonating to return to your admin session.</p>
          <div className={styles.impersonationControls}>
            <select
              value={impersonatedUserId || ''}
              onChange={(event) => {
                const targetUserId = event.target.value;
                if (targetUserId) {
                  startImpersonation(targetUserId);
                } else {
                  stopImpersonation();
                }
              }}
              disabled={isLoadingUsers}
              className={styles.userSelect}
            >
              <option value="">{isLoadingUsers ? 'Loading users...' : 'Not impersonating'}</option>
              {availableUsers.map((availableUser) => (
                <option key={availableUser.$id} value={availableUser.$id}>
                  {availableUser.name || availableUser.email || availableUser.$id} ({availableUser.email || availableUser.$id})
                </option>
              ))}
            </select>
            {impersonatedUserId && (
              <button onClick={stopImpersonation} className={styles.actionButton}>Stop Impersonating</button>
            )}
          </div>
          {impersonatedUserId && (
            <p className={styles.impersonationNotice}>
              Currently impersonating {selectedImpersonationUser?.name || selectedImpersonationUser?.email || impersonatedUserId}.
            </p>
          )}
          {userLoadError && <p className={styles.errorText}>{userLoadError}</p>}
        </div>
        
        <div className={styles.section}>
          <h2>Contract Management</h2>
          <div className={styles.contractControls}>
            <label htmlFor="contract-cutoff-date" className={styles.inputLabel}>Contract cutoff date</label>
            <input
              id="contract-cutoff-date"
              type="date"
              value={contractCutoffDate}
              onChange={(event) => setContractCutoffDate(event.target.value)}
              className={styles.dateInput}
            />
            <button onClick={reduceContractDuration} disabled={isReducingContracts} className={styles.actionButton}>
              {isReducingContracts ? 'Reducing Contracts...' : 'Reduce Contract Duration'}
            </button>
          </div>
        </div>


        <div className={styles.section}>
          <h2>Resigning FA Page Status</h2>
          <div className={styles.buttonGroup}>
            <button onClick={() => updateRfaPageStatus('Selection Process')} disabled={isUpdatingRFAStatus} className={styles.actionButton}>Selection Process</button>
            <button onClick={() => updateRfaPageStatus('Blind Auction')} disabled={isUpdatingRFAStatus} className={styles.actionButton}>Blind Auction</button>
            <button onClick={() => updateRfaPageStatus('Off')} disabled={isUpdatingRFAStatus} className={styles.actionButton}>Off</button>
          </div>
          <p>Current Admin Selection: {rfaPageStatus}</p>
        </div>

        <div className={styles.section}>
          <h2>RFA Auction Management</h2>
          <div className={styles.buttonGroup}>
            <button
              onClick={clearAllRFAFlags}
              disabled={isClearingAllRFAFlags}
              className={styles.actionButton}
            >
              {isClearingAllRFAFlags ? 'Clearing RFA Flags...' : 'Reset All RFA Flags to False'}
            </button>
            <button 
              onClick={exportRFABidsWithProjections} 
              disabled={isExportingRFA}
              className={styles.actionButton}
            >
              {isExportingRFA ? 'Exporting...' : 'Export RFA Bids with Projections'}
            </button>
            
            <button 
              onClick={calculateAndUpdateRFAWinners} 
              disabled={isCalculatingRFAWinners}
              className={styles.actionButton}
            >
              {isCalculatingRFAWinners ? 'Calculating RFA...' : 'Calculate & Update RFA Winners'}
            </button>
          </div>
        </div>

        <div className={styles.section}>
          <h2>Free Agent Auction Management</h2>
          <div className={styles.buttonGroup}>
            {/* NEW Export Button */}
            <button
              onClick={exportFABidsWithProjections}
              disabled={isExportingFA}
              className={styles.actionButton}
            >
              {isExportingFA ? 'Exporting...' : 'Export FA Bids with Projections'}
            </button>

            <button 
              onClick={calculateAndUpdateFAWinners} 
              disabled={isCalculatingFAWinners}
              className={styles.actionButton}
            >
              {isCalculatingFAWinners ? 'Calculating FA...' : 'Calculate & Update Free Agent Winners'}
            </button>
          </div>
        </div>

        <div className={styles.section}>
          <h2>Auction Results (Legacy)</h2>
          <div className={styles.buttonGroup}>
            <button 
              onClick={processRFAAuction} 
              disabled={isProcessingRFA}
              className={styles.actionButton}
            >
              {isProcessingRFA ? 'Processing...' : 'Run RFA Auction Winners'}
            </button>
            
            <button 
              onClick={processFreeAgentAuction} 
              disabled={isProcessingFA}
              className={styles.actionButton}
            >
              {isProcessingFA ? 'Processing...' : 'Run Free Agent Auction Winners'}
            </button>
          </div>
        </div>

        {showRFAResults && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2>RFA Auction Results</h2>
              <button 
                onClick={() => exportResults(rfaWinners, 'RFA')}
                className={styles.exportButton}
              >
                Export CSV
              </button>
            </div>
            <div className={styles.resultsContainer}>
              {rfaWinners.length === 0 ? (
                <p>No RFA bids found.</p>
              ) : (
                <table className={styles.resultsTable}>
                  <thead>
                    <tr>
                      <th>Player</th>
                      <th>Position</th>
                      <th>Winning Bid</th>
                      <th>Duration</th>
                      <th>Winner</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rfaWinners.map((winner, index) => (
                      <tr key={index}>
                        <td>{winner.playerName}</td>
                        <td>{winner.position}</td>
                        <td>${winner.winningBid}</td>
                        <td>{winner.duration} year{winner.duration !== 1 ? 's' : ''}</td>
                        <td>{winner.winner}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {showFAResults && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2>Free Agent Auction Results</h2>
              <button 
                onClick={() => exportResults(freeAgentWinners, 'FreeAgent')}
                className={styles.exportButton}
              >
                Export CSV
              </button>
            </div>
            <div className={styles.resultsContainer}>
              {freeAgentWinners.length === 0 ? (
                <p>No Free Agent bids found.</p>
              ) : (
                <table className={styles.resultsTable}>
                  <thead>
                    <tr>
                      <th>Player</th>
                      <th>Position</th>
                      <th>Winning Bid</th>
                      <th>Duration</th>
                      <th>Winner</th>
                    </tr>
                  </thead>
                  <tbody>
                    {freeAgentWinners.map((winner, index) => (
                      <tr key={index}>
                        <td>{winner.playerName}</td>
                        <td>{winner.position}</td>
                        <td>${winner.winningBid}</td>
                        <td>{winner.duration} year{winner.duration !== 1 ? 's' : ''}</td>
                        <td>{winner.winner}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        <div className={styles.section}>
          <h2>Other Admin Functions</h2>
          <div className={styles.subcontainer}>
            <p>Refresh player database</p>
            <p>Mark resign false</p>
            <p>Show / Hide RFA</p>
            <p>Show / Hide Free Agency</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;
